import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Loader2, MessageSquare, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import ChatAvatar from '@/components/chat/ChatAvatar';
import ChatThread from '@/components/chat/ChatThread';
import { fetchConversations } from '@/data/advisor-chat';
import { MESSAGE_POLL_MS } from '@/components/chat/ChatThread';
import type { ChatConversation } from '@/types/chat';

function when(value: string | null) {
  if (!value) return 'No messages yet';
  const at = new Date(value);
  const today = new Date();
  const sameDay = at.toDateString() === today.toDateString();
  return sameDay
    ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleDateString();
}

export default function MessagesPage() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = React.useState('');

  const conversations = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: fetchConversations,
    refetchInterval: MESSAGE_POLL_MS,
    retry: false,
  });

  const all = conversations.data?.conversations ?? [];
  const selectedId = params.get('c');
  const selected = all.find((row) => row.id === selectedId) ?? null;

  const shown = filter.trim()
    ? all.filter((row) =>
        row.counterparty.toLowerCase().includes(filter.trim().toLowerCase()),
      )
    : all;

  const select = (conversation: ChatConversation) => {
    setParams({ c: conversation.id }, { replace: false });
  };

  const unreadTotal = all.reduce((sum, row) => sum + row.unread, 0);

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Messages</h2>
        <p className="text-muted-foreground">
          {all.length === 0
            ? 'Conversations with the companies you advise'
            : `${all.length} conversation${all.length === 1 ? '' : 's'}` +
              (unreadTotal ? ` · ${unreadTotal} unread` : '')}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-lg border" data-testid="chat-list">
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Find a client…"
                className="h-8 pl-8 text-sm"
                data-testid="chat-filter"
              />
            </div>
          </div>

          <div className="max-h-[calc(100vh-19rem)] overflow-y-auto">
            {conversations.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : shown.length === 0 ? (
              <p className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="chat-empty">
                {all.length === 0
                  ? 'No conversations yet. They start when a client picks you.'
                  : 'No one by that name.'}
              </p>
            ) : (
              shown.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => select(row)}
                  className={`flex w-full items-center gap-3 border-b px-3 py-3 text-left last:border-b-0 hover:bg-muted ${
                    row.id === selectedId ? 'bg-muted' : ''
                  }`}
                  data-testid={`chat-conversation-${row.id}`}
                >
                  <ChatAvatar name={row.counterparty} photoUrl={row.counterparty_photo_url} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{row.counterparty}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {when(row.last_message_at)}
                      {!row.is_open && ' · closed'}
                    </p>
                  </div>
                  {row.unread > 0 && (
                    <Badge className="h-5 min-w-[20px] justify-center px-1.5 text-[10px]">
                      {row.unread}
                    </Badge>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex min-h-[60vh] flex-col rounded-lg border">
          {selected ? (
            <ChatThread
              key={selected.id}
              conversationId={selected.id}
              header={
                <div className="flex items-center gap-2 border-b px-4 py-3">
                  <ChatAvatar
                    name={selected.counterparty}
                    photoUrl={selected.counterparty_photo_url}
                    className="h-9 w-9"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium" data-testid="chat-counterparty">
                      {selected.counterparty}
                    </p>
                    {!selected.is_open && (
                      <p className="text-[11px] text-muted-foreground">Closed</p>
                    )}
                  </div>
                </div>
              }
            />
          ) : (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground"
              data-testid="no-conversation-selected"
            >
              <MessageSquare className="h-8 w-8 opacity-30" />
              <p className="text-sm">
                {all.length === 0
                  ? 'Nothing to read yet'
                  : 'Pick a conversation on the left'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
